/**
 * Liste de la marketplace au téléphone (barre e3 avec la recherche) : quand au moins un filtre ou une
 * recherche est actif, `html.mp-filtered` masque la ligne du titre (le H1 reste dans le HTML), met « Trier »
 * à côté de la recherche et réduit « Sauvegarder la recherche » à sa cloche (`_bar-e3.scss`,
 * `_compact-layout.scss`).
 *
 * Le serveur pose la classe au premier rendu (`MarketplaceChromeHelper::listingIsFiltered`) ; ce module la
 * tient à jour après chaque recherche, sans rechargement, avec la même règle : les filtres comptés par
 * « Filtrer » (`countActiveMarketplaceFilters`, filtre imposé par la page compris) ou une recherche texte.
 */

export const FILTERED_CLASS = "mp-filtered";

/**
 * Placeholder du champ de la barre : raccourci quand « Trier » partage la ligne. Sous 375 px, « Trier » reste
 * écrit (règle d'Alexis du 29/10) et le champ n'a plus que 85 px utiles à 320 px : « Rechercher… ».
 */
export const SEARCH_PLACEHOLDER = "Marque, modèle, finition…";
export const SEARCH_PLACEHOLDER_SHORT = "Marque, modèle…";
export const SEARCH_PLACEHOLDER_NARROW = "Rechercher…";
export const NARROW_QUERY = "(max-width: 374.98px)";

const SEARCH_INPUT_SELECTOR = ".mkt-e3-bar__search .aa-Input";

export function searchPlaceholderFor(filtered: boolean, narrow = false): string {
    if (!filtered) return SEARCH_PLACEHOLDER;

    return narrow ? SEARCH_PLACEHOLDER_NARROW : SEARCH_PLACEHOLDER_SHORT;
}

/** Écran de moins de 375 px (320, 360) : placeholder le plus court quand la liste est filtrée. */
export function isNarrowViewport(win: Pick<Window, "matchMedia"> | undefined = typeof window === "undefined" ? undefined : window): boolean {
    return typeof win?.matchMedia === "function" && win.matchMedia(NARROW_QUERY).matches;
}

/** Liste filtrée au rendu serveur (classe posée par le layout). */
export function isListingFilteredOnLoad(doc: Document = document): boolean {
    return doc.documentElement.classList.contains(FILTERED_CLASS);
}

/** La page porte le « Trier » de la ligne de recherche (liste de la marketplace, barre e3 au téléphone). */
const ROW_SORT_SELECTOR = ".mkt-e3-sort--row";

/** Filtres de l'URL sans pastille ni compteur (`hiddenRefinementsState`, ex. pays fournisseur). */
export function countHiddenRefinements(
    hidden: Record<string, string[] | undefined>,
): number {
    return Object.values(hidden).filter((values) =>
        (values ?? []).some((value) => `${value}`.trim() !== ""),
    ).length;
}

export function isListingFiltered(
    activeFilterCount: number,
    query: string | null | undefined,
): boolean {
    return activeFilterCount > 0 || (query ?? "").trim() !== "";
}

export function syncListingFilteredState(
    activeFilterCount: number,
    query: string | null | undefined,
    doc: Document = document,
): void {
    if (!doc.querySelector(ROW_SORT_SELECTOR)) return;
    const root = doc.documentElement;
    const filtered = isListingFiltered(activeFilterCount, query);
    if (root.classList.contains(FILTERED_CLASS) !== filtered) {
        root.classList.toggle(FILTERED_CLASS, filtered);
    }
    applySearchPlaceholder(doc);
    watchSearchPlaceholder(doc);
}

function applySearchPlaceholder(doc: Document): void {
    const input = doc.querySelector<HTMLInputElement>(SEARCH_INPUT_SELECTOR);
    const placeholder = searchPlaceholderFor(isListingFilteredOnLoad(doc), isNarrowViewport(doc.defaultView ?? undefined));
    if (input && input.placeholder !== placeholder)
        input.placeholder = placeholder;
}

const watchedDocs = new WeakSet<Document>();

/**
 * L'autocomplétion réécrit son placeholder (option passée à sa création) en se rendant à nouveau, ex. après
 * une recherche validée : on remet celui de l'état en cours.
 */
function watchSearchPlaceholder(doc: Document): void {
    const field = doc.querySelector(".mkt-e3-bar__search");
    if (
        !field ||
        watchedDocs.has(doc) ||
        typeof MutationObserver === "undefined"
    )
        return;
    watchedDocs.add(doc);
    new MutationObserver(() => applySearchPlaceholder(doc)).observe(field, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["placeholder"],
    });
    // Rotation de l'écran : le placeholder suit la largeur
    doc.defaultView?.matchMedia?.(NARROW_QUERY).addEventListener?.("change", () => applySearchPlaceholder(doc));
}
